import React, { useState } from 'react';
import useSWR, { mutate } from 'swr';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DeleteIcon from './assets/images/delete-icon.svg';
import DownloadIcon from './assets/images/download_icon.svg';

const fetcher = url => {
    const token = localStorage.getItem('token');
    return fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => {
        if (!res.ok) {
            const error = new Error('Yetkilendirme hatası veya sunucuya ulaşılamadı.');
            error.status = res.status;
            throw error;
        }
        return res.json();
    });
};

const normalizeTr = (str) => (str || '')
    .replace(/ş/g, 's').replace(/Ş/g, 'S')
    .replace(/ı/g, 'i').replace(/İ/g, 'I')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'G')
    .replace(/ü/g, 'u').replace(/Ü/g, 'U')
    .replace(/ö/g, 'o').replace(/Ö/g, 'O')
    .replace(/ç/g, 'c').replace(/Ç/g, 'C');

function TedarikListesi() {
    const { data: liste, error: listeHata } = useSWR('/api/tedarik-listesi', fetcher);
    const { data: talepler, error: taleplerHata } = useSWR('/api/tedarik-talepler', fetcher);

    const [kalipSeciliId, setKalipSeciliId] = useState('');
    const [malzemeSeciliId, setMalzemeSeciliId] = useState('');
    const [editingItemId, setEditingItemId] = useState(null);
    const [adetInputValue, setAdetInputValue] = useState('');
    const [kaydediliyor, setKaydediliyor] = useState({ kalip: false, malzeme: false });

    const isAdmin = (() => {
        try { return JSON.parse(localStorage.getItem('user'))?.rol === 'admin'; }
        catch { return false; }
    })();

    const kalipTalepler = (talepler || []).filter(t => t.talep_turu === 'kalip');
    const malzemeTalepler = (talepler || []).filter(t => t.talep_turu === 'malzeme');
    const kalipPending = liste?.resim_kaliplari || [];
    const malzemePending = liste?.malzemeler || [];

    const handleAdetDuzenle = (item) => {
        setEditingItemId(item.id);
        setAdetInputValue(item.tedarik_adeti);
    };

    const handleAdetKaydet = async (itemId) => {
        const yeniAdet = parseInt(adetInputValue);
        if (isNaN(yeniAdet) || yeniAdet <= 0) { alert('Lütfen geçerli bir adet girin.'); return; }
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`/api/tedarik-listesi/${itemId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ tedarik_adeti: yeniAdet })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.hata);
            mutate('/api/tedarik-listesi');
        } catch (e) { alert(`Hata: ${e.message}`); }
        finally { setEditingItemId(null); }
    };

    const handleUrunSil = async (itemId) => {
        if (!window.confirm('Bu ürünü tedarik listesinden kaldırmak istediğinizden emin misiniz?')) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`/api/tedarik-listesi/${itemId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.hata);
            mutate('/api/tedarik-listesi');
            mutate('/api/tedarik-talepler');
        } catch (e) { alert(`Hata: ${e.message}`); }
    };

    const handleKaydet = async (tur) => {
        setKaydediliyor(prev => ({ ...prev, [tur]: true }));
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const tarihStr = `${day}.${month}.${year}`;
        const turAdi = tur === 'kalip' ? 'Kalıp Talebi' : 'Malzeme Talebi';
        const talepAdi = `${tarihStr} ${turAdi}`;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch('/api/tedarik-talepler', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ talep_turu: tur, talep_adi: talepAdi })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.hata);
            await mutate('/api/tedarik-listesi');
            await mutate('/api/tedarik-talepler');
            if (tur === 'kalip') setKalipSeciliId(String(data.talep.id));
            else setMalzemeSeciliId(String(data.talep.id));
        } catch (e) { alert(`Hata: ${e.message}`); }
        finally { setKaydediliyor(prev => ({ ...prev, [tur]: false })); }
    };

    const handleTalepSil = async (talepId) => {
        if (!window.confirm('Bu kayıtlı talebi silmek istediğinizden emin misiniz? Talebe ait tüm ürünler de silinecektir.')) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`/api/tedarik-talepler/${talepId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.hata);
            if (String(kalipSeciliId) === String(talepId)) setKalipSeciliId('');
            if (String(malzemeSeciliId) === String(talepId)) setMalzemeSeciliId('');
            mutate('/api/tedarik-talepler');
        } catch (e) { alert(`Hata: ${e.message}`); }
    };

    const handleDownloadPdf = (talep) => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pdfBaslik = normalizeTr(talep.talep_adi.replace('Talebi', 'Talep Listesi'));

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(pdfBaslik, pageWidth / 2, 14, { align: 'center' });

        autoTable(doc, {
            startY: 22,
            head: [['Urun Adi', 'Mevcut Stok', 'Talep Adeti', 'Ekleyen', 'Talep Tarihi']],
            body: talep.urunler.map(item => [
                normalizeTr(item.urun_adi),
                item.stok_miktari,
                item.tedarik_adeti,
                normalizeTr(item.ekleyen),
                item.eklenme_tarihi
            ]),
            styles: { font: 'helvetica', fontSize: 11, cellPadding: 4, valign: 'middle' },
            headStyles: { fillColor: [56, 132, 194], textColor: 255, fontStyle: 'bold', valign: 'middle', halign: 'center' },
            columnStyles: {
                0: { halign: 'left' },
                1: { halign: 'center', cellWidth: 36 },
                2: { halign: 'center', cellWidth: 34, fontStyle: 'bold' },
                3: { halign: 'center', cellWidth: 26 },
                4: { halign: 'center', cellWidth: 36 },
            },
            alternateRowStyles: { fillColor: [248, 248, 248] },
            margin: { left: 8, right: 8 },
            didParseCell: (data) => {
                if (data.section === 'head' && data.column.index === 0) {
                    data.cell.styles.halign = 'left';
                }
            },
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
        }
        doc.save(`${normalizeTr(talep.talep_adi).replace(/\s+/g, '_')}.pdf`);
    };

    if (listeHata || taleplerHata) return <div style={{ padding: '20px' }}>Veri yüklenirken hata oluştu.</div>;
    if (!liste || !talepler) return <div style={{ padding: '20px' }}>Yükleniyor...</div>;

    const s = {
        sayfa: { padding: '20px' },
        anaKapsayici: { display: 'flex', gap: '30px', alignItems: 'flex-start' },
        sutun: {
            flex: 1,
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
        },
        sutunBasligi: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 20px',
            borderBottom: '1px solid #eee',
            background: '#fafafa',
        },
        sutunBaslikText: { fontWeight: 'bold', fontSize: '16px', margin: 0 },
        bolumBasligi: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 20px',
            background: '#f0f5fb',
            borderTop: '1px solid #e8eef5',
            borderBottom: '1px solid #e8eef5',
        },
        bolumBaslikText: { fontWeight: '600', fontSize: '12px', color: '#3884C2', margin: 0, letterSpacing: '0.5px', textTransform: 'uppercase' },
        sutunIcerik: { padding: '14px 20px' },
        kayitliSelectSatir: { display: 'flex', alignItems: 'center', gap: '10px' },
        kayitliSelect: {
            padding: '6px 10px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            fontSize: '13px',
            fontFamily: 'poppins',
            flex: 1,
            minWidth: 0,
        },
        indirButonu: {
            padding: '5px 12px',
            fontFamily: 'poppins',
            alignItems: 'center',
            display: 'flex',
            gap: '6px',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },
        kaydetButonu: {
            padding: '5px 14px',
            fontFamily: 'poppins',
            background: 'linear-gradient(90deg, #3884C2 0%, #255380 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },
        tablo: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '10px' },
        th: { padding: '10px 12px', fontWeight: '500', textAlign: 'left', background: '#f2f2f2', borderBottom: '2px solid #e0e0e0' },
        thCenter: { padding: '10px 12px', fontWeight: '500', textAlign: 'center', background: '#f2f2f2', borderBottom: '2px solid #e0e0e0' },
        td: { padding: '10px 12px', borderBottom: '1px solid #eee' },
        tdCenter: { padding: '10px 12px', borderBottom: '1px solid #eee', textAlign: 'center' },
        emptyMsg: { padding: '20px 0', textAlign: 'center', color: '#aaa', fontSize: '13px' },
        silButon: (disabled) => ({
            background: 'none', border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.25 : 1,
            padding: '2px',
        }),
    };

    const renderTable = (items, isSaved) => (
        items.length === 0
            ? <p style={s.emptyMsg}>Kayıt bulunamadı.</p>
            : (
                <table style={s.tablo}>
                    <thead>
                        <tr>
                            <th style={s.th}>Ürün Adı</th>
                            <th style={s.thCenter}>Mevcut Stok</th>
                            <th style={s.thCenter}>Talep Adeti</th>
                            <th style={s.thCenter}>Ekleyen</th>
                            <th style={s.thCenter}>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(item => (
                            <tr key={item.id}>
                                <td style={s.td}>{item.urun_adi}</td>
                                <td style={s.tdCenter}>{item.stok_miktari}</td>
                                <td style={{ ...s.tdCenter, cursor: !isSaved ? 'pointer' : 'default' }}
                                    onClick={() => !isSaved && handleAdetDuzenle(item)}>
                                    {editingItemId === item.id && !isSaved ? (
                                        <input type="number" value={adetInputValue}
                                            onChange={e => setAdetInputValue(e.target.value)}
                                            onBlur={() => handleAdetKaydet(item.id)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleAdetKaydet(item.id); }}
                                            autoFocus style={{ width: '60px', textAlign: 'center' }} />
                                    ) : <span>{item.tedarik_adeti}</span>}
                                </td>
                                <td style={s.tdCenter}>{item.ekleyen_kullanici || item.ekleyen}</td>
                                <td style={s.tdCenter}>
                                    <button
                                        disabled={isSaved && !isAdmin}
                                        onClick={() => handleUrunSil(item.id)}
                                        title={isSaved && !isAdmin ? 'Sadece admin silebilir' : 'Kaldır'}
                                        style={s.silButon(isSaved && !isAdmin)}>
                                        <img src={DeleteIcon} alt="Sil" style={{ width: '20px' }} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
    );

    const renderSutun = (title, tur, savedTalepler, pendingItems, seciliId, setSeciliId) => {
        const seciliTalep = savedTalepler.find(t => String(t.id) === seciliId) || null;
        return (
            <div style={s.sutun}>
                {/* Sütun başlığı */}
                <div style={s.sutunBasligi}>
                    <h3 style={s.sutunBaslikText}>{title}</h3>
                    {pendingItems.length > 0 && (
                        <button style={s.kaydetButonu}
                            onClick={() => handleKaydet(tur)}
                            disabled={kaydediliyor[tur]}>
                            {kaydediliyor[tur] ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    )}
                </div>

                {/* Kayıtlı Talepler bölümü */}
                <div style={s.bolumBasligi}>
                    <span style={s.bolumBaslikText}>Kayıtlı Talepler</span>
                    {seciliTalep && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button style={s.indirButonu} onClick={() => handleDownloadPdf(seciliTalep)}>
                                <img src={DownloadIcon} alt="İndir" style={{ height: '14px' }} />
                                <span>İndir</span>
                            </button>
                            {isAdmin && (
                                <button onClick={() => handleTalepSil(seciliTalep.id)}
                                    title="Talebi Sil" style={s.silButon(false)}>
                                    <img src={DeleteIcon} alt="Sil" style={{ width: '18px' }} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div style={s.sutunIcerik}>
                    <div style={s.kayitliSelectSatir}>
                        <select style={s.kayitliSelect} value={seciliId}
                            onChange={e => setSeciliId(e.target.value)}>
                            <option value="">-- Talep Seçin --</option>
                            {savedTalepler.map(t => (
                                <option key={t.id} value={String(t.id)}>{t.talep_adi}</option>
                            ))}
                        </select>
                    </div>
                    {seciliTalep && renderTable(seciliTalep.urunler, true)}
                </div>

                {/* Yeni Talep bölümü */}
                <div style={s.bolumBasligi}>
                    <span style={s.bolumBaslikText}>Yeni Talep</span>
                </div>
                <div style={s.sutunIcerik}>
                    {renderTable(pendingItems, false)}
                </div>
            </div>
        );
    };

    return (
        <div style={s.sayfa}>
            <div style={s.anaKapsayici}>
                {renderSutun('BASKI KALIP TALEPLERİ', 'kalip', kalipTalepler, kalipPending, kalipSeciliId, setKalipSeciliId)}
                {renderSutun('MALZEME TALEPLERİ', 'malzeme', malzemeTalepler, malzemePending, malzemeSeciliId, setMalzemeSeciliId)}
            </div>
        </div>
    );
}

export default TedarikListesi;
